import { fetchApi } from './api';
import type {
    PbiReportRow, PbiUsageStats, PbiUsageFilterOptions, PbiUsageFilters, PbiUsageWindow,
    PbiReportDetail, PbiReviewStatus, PbiReviewSetResult, PbiSortField, SortOrder,
} from '../types/powerBiUsage';

interface PaginatedResponse<T> {
    items: T[];
    total: number;
}

interface ListParams {
    page: number;
    limit: number;
    sortBy: PbiSortField;
    sortOrder: SortOrder;
    window: PbiUsageWindow;
    filters: PbiUsageFilters;
}

function applyFilters(sp: URLSearchParams, filters: PbiUsageFilters, window: PbiUsageWindow): void {
    sp.set('window', window);
    if (filters.workspaceId) sp.set('workspaceId', filters.workspaceId);
    if (filters.search) sp.set('search', filters.search);
    if (filters.reviewStatus) sp.set('reviewStatus', filters.reviewStatus);
    if (filters.usage) sp.set('usage', filters.usage);
    if (filters.includePersonal) sp.set('includePersonal', '1');
    if (filters.includeDeleted) sp.set('includeDeleted', '1');
}

export const powerBiUsageApi = {
    async list(params: ListParams): Promise<PaginatedResponse<PbiReportRow>> {
        const sp = new URLSearchParams();
        sp.set('page', String(params.page));
        sp.set('limit', String(params.limit));
        sp.set('sortBy', params.sortBy);
        sp.set('sortOrder', params.sortOrder);
        applyFilters(sp, params.filters, params.window);

        // Raw fetch (not fetchApi) so we can read the pagination wrapper for total.
        const response = await fetch(`/api/v1/powerbi-usage/reports?${sp}`, {
            headers: { 'Content-Type': 'application/json' },
        });
        const body = await response.json();
        if (!response.ok || !body.success) {
            throw new Error(body.error?.message || `API error: ${response.status}`);
        }
        return {
            items: body.data as PbiReportRow[],
            total: body.pagination?.total ?? 0,
        };
    },

    getStats(filters: PbiUsageFilters, window: PbiUsageWindow): Promise<PbiUsageStats> {
        // Stats respect the SCOPE (window/workspace/personal/deleted) but not the tile
        // filters — mirror that here so tiles describe the population.
        const sp = new URLSearchParams();
        sp.set('window', window);
        if (filters.workspaceId) sp.set('workspaceId', filters.workspaceId);
        if (filters.includePersonal) sp.set('includePersonal', '1');
        if (filters.includeDeleted) sp.set('includeDeleted', '1');
        return fetchApi<PbiUsageStats>(`/powerbi-usage/stats?${sp}`);
    },

    getFilters(): Promise<PbiUsageFilterOptions> {
        return fetchApi<PbiUsageFilterOptions>('/powerbi-usage/filters');
    },

    getDetail(reportId: string, window: PbiUsageWindow): Promise<PbiReportDetail> {
        return fetchApi<PbiReportDetail>(
            `/powerbi-usage/reports/${encodeURIComponent(reportId)}?window=${window}`,
        );
    },

    setReview(reportId: string, payload: { status: PbiReviewStatus; statusNote?: string | null }): Promise<PbiReviewSetResult> {
        return fetchApi<PbiReviewSetResult>(
            `/powerbi-usage/reports/${encodeURIComponent(reportId)}/review`,
            { method: 'PUT', body: JSON.stringify(payload) },
        );
    },
};
